import type { FieldValues, RecordRef } from '../../models/company-data.js';
import type { DataField } from './definition.js';
import type { ReferenceOption } from './form-values.js';

// job/read gives a reference the job covers as its record and title, and any other as hidden
type JobReference = RecordRef & { title: string };

function isJobReference(value: unknown): value is JobReference {
  return typeof value === 'object' && value !== null && typeof (value as JobReference).recordId === 'string';
}

// A job's values as the record form takes them: covered references as plain refs with their titles, hidden ones left out
export function jobFormValues(fields: DataField[], values: FieldValues) {
  const formValues: FieldValues = {};
  const titles = new Map<string, string>();
  const hidden: string[] = [];
  for (const [key, value] of Object.entries(values)) {
    if (fields.find((field) => field.key === key)?.kind !== 'reference') formValues[key] = value;
    else if (isJobReference(value)) {
      formValues[key] = { setId: value.setId, recordId: value.recordId, sourceId: null };
      titles.set(value.recordId, value.title);
    } else hidden.push(key);
  }
  return { values: formValues, titles, hidden };
}

// What a reference field picks from: the records of the set the job covers for them, and whatever it points at now
export function jobReferenceOptions(
  fields: DataField[],
  linked: { setId: string; records: { id: string; title: string }[] } | undefined,
  current: FieldValues,
  titles: Map<string, string>,
): Record<string, ReferenceOption[]> {
  const fromLinked: ReferenceOption[] = linked
    ? linked.records.map((record) => ({
        ref: { setId: linked.setId, recordId: record.id, sourceId: null },
        label: record.title || 'Untitled',
      }))
    : [];
  return Object.fromEntries(
    fields
      .filter((field) => field.kind === 'reference')
      .map((field) => {
        const value = current[field.key] as RecordRef | undefined;
        const known = !value || fromLinked.some((option) => option.ref.recordId === value.recordId);
        const now = known ? [] : [{ ref: value, label: titles.get(value.recordId) || 'Untitled' }];
        return [field.key, [...now, ...fromLinked]];
      }),
  );
}

// A reference the job can't show still has a value, which a new pick replaces
export function hiddenReferenceHelp(fields: DataField[], hidden: string[]): DataField[] {
  return fields.map((field) =>
    hidden.includes(field.key)
      ? {
          ...field,
          help: [field.help, "It points at a record this job doesn't show. Picking one replaces it."]
            .filter(Boolean)
            .join(' '),
        }
      : field,
  );
}

// "51–100 of 230", or nothing to count
export function pageLabel(page: number, pageSize: number, total: number): string {
  if (total === 0) return '';
  const first = (page - 1) * pageSize + 1;
  return `${first}–${Math.min(page * pageSize, total)} of ${total}`;
}
